import { App as CapacitorApp } from '@capacitor/app'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import './index.css'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { Providers } from './providers'
import { useAuth } from '@/hooks/useAuth'
import { useGlobalOrganization } from '@/hooks/useGlobalOrganization'
import { LoginPage } from '@/components/LoginPage'
import { SelectOrganizationPage } from '@/components/SelectOrganizationPage'
import { Dashboard } from '@/components/Dashboard'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { PublicInvoicePage } from '@/components/public/PublicInvoicePage'
import { PublicEstimatePage } from '@/components/public/PublicEstimatePage'
import { LegalFooter } from '@/components/LegalFooter'
import { DeleteAccountPage } from '@/components/public/DeleteAccountPage'
import { SignupCompletePage } from '@/components/public/SignupCompletePage'
import { StripeCallbackPage } from '@/components/StripeCallbackPage'
import { useIsRestoring } from '@tanstack/react-query'
import { RealtimeProvider } from '@/providers/RealtimeProvider'
import { useNotifications } from '@/components/ui/NotificationSystem'
import { attachOtaLifecycleListeners, checkAndInstall, signalAppReady } from '@/lib/otaUpdater'
import { isAndroid, isNative } from '@/lib/capacitor'
import { useNativeKeyboardFix } from '@/hooks/useNativeKeyboardFix'
import { OfflineBanner } from '@/components/OfflineBanner'
import { TapToPayWarmup } from '@/components/TapToPayWarmup'
import { TapToPaySplash } from '@/components/TapToPaySplash'
import { NativeAppUpdateModal } from '@/components/NativeAppUpdateModal'
import {
  checkNativeAppUpdate,
  isNativeAppUpdateSnoozed,
  snoozeNativeAppUpdate,
  type NativeUpdateDecision,
} from '@/lib/nativeAppUpdate'

type AvailableUpdateDecision = Exclude<NativeUpdateDecision, { kind: 'none' }>

function UpdateBootstrap({ children }: { children: ReactNode }) {
  const { addNotification } = useNotifications()
  const [nativeUpdate, setNativeUpdate] = useState<AvailableUpdateDecision | null>(null)
  const nativeUpdateRef = useRef<AvailableUpdateDecision | null>(null)

  const applyDecision = useCallback((decision: NativeUpdateDecision) => {
    const nextDecision = decision.kind === 'none' ? null : decision
    nativeUpdateRef.current = nextDecision
    setNativeUpdate(nextDecision)
  }, [])

  useEffect(() => {
    if (!isNative()) {
      return
    }

    const notify = ({
      type,
      title,
      message,
    }: {
      type: 'info' | 'success' | 'error' | 'warning'
      title: string
      message?: string
    }) => {
      addNotification({
        type,
        title,
        message,
        duration: type === 'success' ? 8000 : type === 'error' ? 6000 : 4000,
      })
    }

    const detachListeners = isAndroid() ? attachOtaLifecycleListeners(notify) : () => {}
    let cancelled = false

    const checkForUpdates = async () => {
      const existingRequiredUpdate = nativeUpdateRef.current?.kind === 'required'
      const outcome = await checkNativeAppUpdate()
      if (cancelled) {
        return
      }

      // A required decision remains sticky across transient store/policy failures.
      // Only a positive "current" response, or a successfully refreshed policy,
      // may release or downgrade the gate.
      if (existingRequiredUpdate) {
        if (outcome.storeStatus === 'current') {
          applyDecision({ kind: 'none' })
          if (isAndroid()) {
            await checkAndInstall(notify)
          }
        } else if (
          outcome.storeStatus === 'available' &&
          outcome.policyStatus === 'available'
        ) {
          applyDecision(outcome.decision)
        }
        return
      }

      if (outcome.storeStatus === 'available' && outcome.decision.kind !== 'none') {
        const snoozed = await isNativeAppUpdateSnoozed(outcome.decision)
        if (!cancelled && !snoozed) {
          applyDecision(outcome.decision)
        }
        return
      }

      if (outcome.storeStatus === 'current') {
        applyDecision({ kind: 'none' })
      }
      if (isAndroid()) {
        await checkAndInstall(notify)
      }
    }

    const resumeListener = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        void checkForUpdates()
      }
    })

    void (async () => {
      if (isAndroid()) {
        // Commit the freshly-activated bundle FIRST so capgo never rolls it back to
        // builtin while we do store or OTA network work.
        await signalAppReady()
      }

      await checkForUpdates()
    })()

    return () => {
      cancelled = true
      detachListeners()
      void resumeListener.then((handle) => handle.remove())
    }
  }, [addNotification, applyDecision])

  if (!nativeUpdate) {
    return children
  }

  return (
    <NativeAppUpdateModal
      decision={nativeUpdate}
      onLater={() => {
        const decision = nativeUpdateRef.current
        applyDecision({ kind: 'none' })
        if (decision?.kind === 'optional') {
          void snoozeNativeAppUpdate(decision)
        }
      }}
      onStoreOpened={() => applyDecision({ kind: 'none' })}
    />
  )
}

/*
 * TODO(full-height-screens): the loading / login / org-picker screens below use `min-h-screen`,
 * which means "at least as tall as the screen" using an older way of measuring. On phone web
 * browsers that measurement includes the strip hidden behind the browser's own address bar, so
 * the page comes out slightly taller than what you can actually see and content can sit a little
 * low on the screen.
 *
 * This does NOT affect the installed iOS or Android apps. They have no address bar, so the old
 * measurement is already correct there. It only shows up in the browser version, and even there
 * it is mild — these screens are just a centred spinner or form.
 *
 * The modern replacement is `100dvh`, which measures only the part you can see. The app's main
 * screens already use it (see `.screen-container` in index.css).
 *
 * Two things to know before changing it:
 *
 * 1. Changing it HERE will not work on its own. Tailwind only includes styles it finds by
 *    scanning a specific list of files, and this file is not on that list — so a new style name
 *    written here is silently dropped and these screens would end up with NO minimum height,
 *    which looks worse than the current problem. The list has to be widened first; see
 *    TODO(css-scanning) in tailwind.config.js, which also records that doing so was measured
 *    as safe.
 * 2. The most worthwhile place to fix is not here anyway. It is the two customer-facing pages,
 *    src/components/public/PublicInvoicePage.tsx and PublicEstimatePage.tsx, which customers open
 *    in a real mobile browser. Those live in a folder that IS scanned, so they can be fixed on
 *    their own with no config change at all.
 *
 * Left alone deliberately — nothing looks wrong today.
 */
function AuthenticatedApp() {
  const { isAuthenticated, isLoading } = useAuth()
  const { orgReady, isLoading: orgsLoading } = useGlobalOrganization()
  const location = useLocation()
  // While the persisted query cache is rehydrating from disk, org/company data
  // isn't available yet — wait so we don't flash "no organization" on offline cold start.
  const isRestoring = useIsRestoring()

  if (isLoading || isRestoring) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="text-center animate-fade-in">
          <LoadingSpinner />
          <p className="mt-4 text-secondary-600 font-medium">Loading Service Strike...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-secondary-50">
        <LoginPage />
      </div>
    )
  }

  // Stripe OAuth callback — dedicated page outside Dashboard
  if (location.pathname === '/payment-callback') {
    return <StripeCallbackPage />
  }

  // Wait for organizations to load
  if (orgsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="text-center animate-fade-in">
          <LoadingSpinner />
          <p className="mt-4 text-secondary-600 font-medium">Loading organizations...</p>
        </div>
      </div>
    )
  }

  // Organization not selected yet — show selector (handles 0 orgs error and multi-org picker)
  if (!orgReady) {
    return <SelectOrganizationPage />
  }

  return (
    <RealtimeProvider>
      <Dashboard />
    </RealtimeProvider>
  )
}

function AppContent() {
  useNativeKeyboardFix()

  return (
    <Providers>
      <UpdateBootstrap>
        <TapToPayWarmup />
        <TapToPaySplash />
        <OfflineBanner />
        <AuthenticatedApp />
      </UpdateBootstrap>
    </Providers>
  )
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Footer sits outside the page so it survives every render state, including the
            loading and expired-link screens a carrier URL scanner is most likely to hit. */}
        <Route path="/public/invoice/:invoiceId/:token" element={<><PublicInvoicePage /><LegalFooter /></>} />
        <Route path="/public/estimates/:estimateId/:token" element={<><PublicEstimatePage /><LegalFooter /></>} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/signup/complete" element={<SignupCompletePage />} />
        <Route path="/*" element={<AppContent />} />
      </Routes>
    </Router>
  )
}

export default App
